import React, { useState, useEffect, useMemo } from 'react';
import { ArrowDownTrayIcon, PrinterIcon, DocumentChartBarIcon, CalendarIcon } from '@heroicons/react/24/outline';
import HelpIcon from './HelpIcon';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

interface ProductGroup {
  id: string;
  groupName: string;
}

interface ReportData {
  stationnumber: number;
  employee_name: string;
  infeed_weight: number;
  outfeed_weight: number;
  yield_percent: number;
  time_worked: number;
  pocket_weights?: Record<string, number>;
  // Computed fields
  pph?: number;
  pcpm?: number;
  // Additional context fields
  productionDate?: string;
  shift?: string;
  lineName?: string;
  productGroupName?: string;
}

interface System {
  id: string;
  systemName: string;
  lineIds?: string[];
}

interface Line {
  id: string;
  lineName: string;
  lineNumber: number;
}

interface ReportPeriod {
  startDate: string;
  endDate: string;
  shift: string;
  productGroup: string;
  line: string;
}

interface ReportSummary {
  totalInfeed: number;
  totalOutfeed: number;
  averageYield: number;
  totalTimeHours: number;
  employeeCount: number;
  stationCount: number;
  reportPeriod?: ReportPeriod;
}

interface ColumnConfig {
  id: string;
  type: string;
  displayName: string;
  productId?: string;
  enabled: boolean;
  order: number;
}

const ReportsPageImproved: React.FC = () => {
  const [startDate, setStartDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [selectedShift, setSelectedShift] = useState<string>('all');
  const [selectedProductGroup, setSelectedProductGroup] = useState<string>('all');
  const [selectedSystem, setSelectedSystem] = useState<string>('all');
  const [selectedLine, setSelectedLine] = useState<string>('all');
  const [productGroups, setProductGroups] = useState<ProductGroup[]>([]);
  const [systems, setSystems] = useState<System[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [reportData, setReportData] = useState<ReportData[]>([]);
  const [reportSummary, setReportSummary] = useState<ReportSummary | null>(null);
  const [columnConfig, setColumnConfig] = useState<ColumnConfig[]>([]);
  const [productNames, setProductNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch product groups, systems and lines on mount
  useEffect(() => {
    fetchProductGroups();
    fetchSystems();
    fetchLines();
  }, []);

  const fetchProductGroups = async () => {
    try {
      const response = await fetch('/api/product-groups');
      if (!response.ok) throw new Error('Failed to fetch product groups');
      const data = await response.json();
      const groups = Array.isArray(data) ? data : (data.data || []);
      setProductGroups(groups.map((g: any) => ({
        id: g.id,
        groupName: g.groupname || g.groupName
      })));
    } catch (err) {
      console.error('Error fetching product groups:', err);
      setError('Failed to load product groups');
    }
  };

  const fetchSystems = async () => {
    try {
      const response = await fetch('/api/systems');
      if (!response.ok) throw new Error('Failed to fetch systems');
      const data = await response.json();
      const systemsData = Array.isArray(data) ? data : (data.data || []);
      setSystems(systemsData.map((s: any) => ({
        id: s.id,
        systemName: s.systemname || s.systemName || `System ${s.id}`,
        lineIds: s.metadata?.lineIds || []
      })));
    } catch (err) {
      console.error('Error fetching systems:', err);
      setError('Failed to load systems');
    }
  };

  const fetchLines = async () => {
    try {
      const response = await fetch('/api/lines');
      if (!response.ok) throw new Error('Failed to fetch lines');
      const data = await response.json();
      const linesData = Array.isArray(data) ? data : (data.data || []);
      setLines(linesData.map((l: any) => ({
        id: l.id,
        lineName: l.linename || l.lineName || `Line ${l.linenumber || l.lineNumber}`,
        lineNumber: l.linenumber || l.lineNumber
      })));
    } catch (err) {
      console.error('Error fetching lines:', err);
      setError('Failed to load lines');
    }
  };

  const generateReport = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
        shift: selectedShift,
        productGroupId: selectedProductGroup
      });

      // Add either system or line parameter
      if (selectedSystem !== 'all') {
        params.append('systemId', selectedSystem);
      } else if (selectedLine !== 'all') {
        params.append('lineId', selectedLine);
      }

      const response = await fetch(`/api/reports/production?${params}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate report');
      }

      const result = await response.json();
      // Backend returns data.data structure
      const data = result.data || {};
      setReportData(data.records || []);
      setReportSummary(data.summary || null);
      setColumnConfig(data.columns || []);
      setProductNames(data.productNames || {});
    } catch (err) {
      console.error('Error generating report:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Build dynamic columns based on configuration
  const tableColumns = useMemo(() => {
    // Use columns directly from API response if available
    if (columnConfig && columnConfig.length > 0) {
      // The API now returns properly formatted columns with correct keys
      return columnConfig.map(col => ({
        ...col,
        align: col.type === 'number' || col.type === 'percent' ? 'center' : 'left'
      }));
    }

    // Fallback to basic columns if no dynamic configuration
    return [
      { key: 'stationnumber', label: 'Station', type: 'number', align: 'left' },
      { key: 'employee_name', label: 'Employee', type: 'text', align: 'left' },
      { key: 'infeed_weight', label: 'Infeed (LB)', type: 'number', align: 'center' },
      { key: 'outfeed_weight', label: 'Total Outfeed (LB)', type: 'number', align: 'center' },
      { key: 'yield_percent', label: 'Total Yield (%)', type: 'number', align: 'center' },
      { key: 'time_worked', label: 'Time (Min)', type: 'number', align: 'center' }
    ];
  }, [columnConfig]);

  const getCellValue = (row: ReportData, column: any) => {
    // Simply get the value using the column key directly
    // The API now provides all data with the exact column keys
    const value = row[column.key as keyof ReportData];

    if (value == null) return '';

    // Format numbers with 2 decimal places
    if (column.type === 'number' && typeof value === 'number') {
      return value.toFixed(column.decimals || 2);
    }

    return value.toString();
  };

  const handleExportCSV = () => {
    if (reportData.length === 0) {
      alert('No data to export. Please generate a report first.');
      return;
    }

    // Build CSV headers
    const headers = tableColumns.map(col => col.label);
    const csvContent = [
      headers.join(','),
      ...reportData.map(row =>
        tableColumns.map(col => getCellValue(row, col)).join(',')
      )
    ].join('\n');

    downloadFile(csvContent, `report_${startDate}_${endDate}.csv`, 'text/csv');
  };

  const handleExportExcel = () => {
    if (reportData.length === 0) {
      alert('No data to export. Please generate a report first.');
      return;
    }

    const ws = XLSX.utils.json_to_sheet(
      reportData.map(row => {
        const obj: any = {};
        tableColumns.forEach(col => {
          obj[col.label] = getCellValue(row, col);
        });
        return obj;
      })
    );

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    XLSX.writeFile(wb, `report_${startDate}_${endDate}.xlsx`);
  };

  const handleExportPDF = () => {
    if (reportData.length === 0) {
      alert('No data to export. Please generate a report first.');
      return;
    }

    const doc = new jsPDF('landscape');

    // Add header with report period info
    doc.setFontSize(16);
    doc.text('Production Report', 14, 15);

    if (reportSummary?.reportPeriod) {
      const period = reportSummary.reportPeriod;
      doc.setFontSize(10);
      doc.text(`Period: ${period.startDate} to ${period.endDate}`, 14, 22);
      doc.text(`Shift: ${period.shift} | Product Group: ${period.productGroup}`, 14, 27);
      doc.text(`Line: ${period.line}`, 14, 32);
    }

    // Prepare table data
    const tableData = reportData.map(row =>
      tableColumns.map(col => getCellValue(row, col))
    );

    (doc as any).autoTable({
      head: [tableColumns.map(col => col.label)],
      body: tableData,
      startY: 40,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [51, 65, 85] }
    });

    // Add summary
    if (reportSummary) {
      const finalY = (doc as any).lastAutoTable.finalY + 10;
      doc.setFontSize(12);
      doc.text('Summary', 14, finalY);

      doc.setFontSize(10);
      doc.text(`Total Infeed: ${reportSummary.totalInfeed.toFixed(2)} LB`, 14, finalY + 7);
      doc.text(`Total Outfeed: ${reportSummary.totalOutfeed.toFixed(2)} LB`, 14, finalY + 12);
      doc.text(`Average Yield: ${reportSummary.averageYield.toFixed(2)}%`, 14, finalY + 17);
      doc.text(`Total Hours: ${reportSummary.totalTimeHours.toFixed(2)}`, 100, finalY + 7);
      doc.text(`Employees: ${reportSummary.employeeCount}`, 100, finalY + 12);
      doc.text(`Stations: ${reportSummary.stationCount}`, 100, finalY + 17);
    }

    doc.save(`report_${startDate}_${endDate}.pdf`);
  };

  const handlePrint = () => {
    if (reportData.length === 0) {
      alert('No data to print. Please generate a report first.');
      return;
    }

    window.print();
  };

  const downloadFile = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 md:p-8 space-y-6 md:space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-800">Production Reports</h1>
          <HelpIcon section="reports/yield-reports" tooltip="Learn about reports and analytics" />
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-800 flex items-center">
            <CalendarIcon className="w-5 h-5 mr-2" />
            Report Filters
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate}
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Shift</label>
            <select
              value={selectedShift}
              onChange={(e) => setSelectedShift(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Shifts</option>
              <option value="SHIFT_1">Shift 1</option>
              <option value="SHIFT_2">Shift 2</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">System</label>
            <select
              value={selectedSystem}
              onChange={(e) => {
                setSelectedSystem(e.target.value);
                // Reset line selection when system is selected
                if (e.target.value !== 'all') {
                  setSelectedLine('all');
                }
              }}
              disabled={selectedLine !== 'all'}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                selectedLine !== 'all'
                  ? 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed'
                  : 'border-slate-300 bg-white'
              }`}
            >
              <option value="all">All Systems</option>
              {systems.map(system => (
                <option key={system.id} value={system.id}>{system.systemName}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Line</label>
            <select
              value={selectedLine}
              onChange={(e) => {
                setSelectedLine(e.target.value);
                // Reset system selection when line is selected
                if (e.target.value !== 'all') {
                  setSelectedSystem('all');
                }
              }}
              disabled={selectedSystem !== 'all'}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                selectedSystem !== 'all'
                  ? 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed'
                  : 'border-slate-300 bg-white'
              }`}
            >
              <option value="all">All Lines</option>
              {lines
                .sort((a, b) => a.lineNumber - b.lineNumber)
                .map(line => (
                  <option key={line.id} value={line.id}>{line.lineName}</option>
                ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Product Group</label>
            <select
              value={selectedProductGroup}
              onChange={(e) => setSelectedProductGroup(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Product Groups</option>
              {productGroups.map(group => (
                <option key={group.id} value={group.id}>{group.groupName}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 mt-4">
          <button
            onClick={generateReport}
            disabled={loading}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Generating...' : 'Generate Report'}
          </button>

          {reportData.length > 0 && (
            <>
              <button
                onClick={handleExportCSV}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
              >
                <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                Export to CSV
              </button>
              <button
                onClick={handleExportExcel}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
              >
                <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                Export to Excel
              </button>
              <button
                onClick={handleExportPDF}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center"
              >
                <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                Export to PDF
              </button>
              <button
                onClick={handlePrint}
                className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors flex items-center"
              >
                <PrinterIcon className="w-4 h-4 mr-2" />
                Print
              </button>
            </>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Report Results */}
      {reportData.length > 0 && (
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Report Details</h2>

          {/* Summary Section with Report Period */}
          {reportSummary && (
            <div className="mb-6 p-4 bg-slate-50 rounded-lg">
              <h3 className="font-medium text-slate-800 mb-3">Summary</h3>

              {/* Report Period Info */}
              {reportSummary.reportPeriod && (
                <div className="mb-3 p-3 bg-white rounded border border-slate-200">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    <div>
                      <span className="text-slate-500">Period:</span>
                      <span className="ml-2 font-medium">{reportSummary.reportPeriod.startDate} to {reportSummary.reportPeriod.endDate}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Shift:</span>
                      <span className="ml-2 font-medium">{reportSummary.reportPeriod.shift}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Product Group:</span>
                      <span className="ml-2 font-medium">{reportSummary.reportPeriod.productGroup}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Line:</span>
                      <span className="ml-2 font-medium">{reportSummary.reportPeriod.line}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Summary Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div>
                  <p className="text-xs text-slate-500">Total Infeed</p>
                  <p className="text-lg font-semibold text-slate-800">{(reportSummary.totalInfeed || 0).toFixed(2)} LB</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Total Outfeed</p>
                  <p className="text-lg font-semibold text-slate-800">{(reportSummary.totalOutfeed || 0).toFixed(2)} LB</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Average Yield</p>
                  <p className="text-lg font-semibold text-slate-800">{(reportSummary.averageYield || 0).toFixed(2)}%</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Total Hours</p>
                  <p className="text-lg font-semibold text-slate-800">{(reportSummary.totalTimeHours || 0).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Employees</p>
                  <p className="text-lg font-semibold text-slate-800">{reportSummary.employeeCount}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Stations</p>
                  <p className="text-lg font-semibold text-slate-800">{reportSummary.stationCount}</p>
                </div>
              </div>
            </div>
          )}

          {/* Data Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-slate-600">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                <tr>
                  {tableColumns.map((col, idx) => (
                    <th key={idx} className={`px-4 py-3 ${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : ''}`}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reportData.map((row, index) => (
                  <tr key={index} className="bg-white border-b border-slate-200 hover:bg-slate-50">
                    {tableColumns.map((col, colIdx) => (
                      <td key={colIdx} className={`px-4 py-3 ${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : ''}`}>
                        {getCellValue(row, col)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No Data Message */}
      {!loading && reportData.length === 0 && !error && (
        <div className="bg-white p-8 rounded-xl shadow-lg text-center">
          <DocumentChartBarIcon className="w-12 h-12 mx-auto text-slate-400 mb-4" />
          <p className="text-slate-600">No report data available. Please select filters and generate a report.</p>
        </div>
      )}
    </div>
  );
};

export default ReportsPageImproved;